import React from "react";
import { ArrowLeft, ArrowRightLeft, UtensilsCrossed } from "lucide-react";

// O cliente leu o QR de uma mesa VAZIA, mas já está em outra mesa aberta
// (dono, 02/10/2026). Quem abriu a mesa pode levar a conta inteira para cá;
// os outros só veem o aviso — a mesa é uma só e quem muda é quem abriu.
//
// Mudar e voltar só existem no aparelho que guarda a sessão da mesa dele
// (`chave`). Quem chegou aqui só pelo CPF, em outro navegador, vê em que mesa
// está e mais nada: o servidor não entrega a mesa de ninguém a um CPF digitado.
const nomeDe = (mesa) => String(mesa?.nome || `Mesa ${mesa?.numero ?? ""}`).toUpperCase();

const MOTIVOS = {
  convidado: (m) => `Só ${m.anfitriao || "quem abriu a mesa"} pode mudar a mesa de lugar. Peça para ${m.anfitriao || "quem abriu"} ler este QR Code.`,
  fechamento: () => "A conta da sua mesa já foi pedida. Chame o atendente para mudar de mesa.",
  divisao: () => "O caixa está fechando a conta da sua mesa. Chame o atendente para mudar de mesa.",
};

const explicar = (minhaMesa, temSessao) => {
  if (!minhaMesa?.pode_mudar) return (MOTIVOS[minhaMesa?.motivo] || MOTIVOS.convidado)(minhaMesa || {});
  if (temSessao) return "Seus pedidos, o que já foi pago e todo mundo que está com você vêm junto para esta mesa.";
  return "Para mudar para esta mesa, leia este QR Code no mesmo celular e navegador em que você abriu a sua. Se não der, chame o atendente.";
};

export default function TelaOutraMesa({ mesa, minhaMesa, loja, logo, darkMode, mudando, onMudar, onVoltar, onFechar }) {
  const fundo = darkMode ? "bg-zinc-950 text-zinc-100" : "bg-white text-zinc-900";
  const borda = darkMode ? "border-zinc-700 text-zinc-200" : "border-zinc-300 text-zinc-700";
  const minha = nomeDe(minhaMesa?.mesa);
  const esta = nomeDe(mesa);
  const temSessao = !!(minhaMesa?.chave && minhaMesa?.mesa?.token);
  const podeMudar = temSessao && !!minhaMesa?.pode_mudar;
  const explicacao = explicar(minhaMesa, temSessao);

  return (
    <div className={`fixed inset-0 z-[60] flex flex-col items-center justify-center px-7 text-center ${fundo}`}
         data-e2e="mesa-outra" data-pode-mudar={podeMudar ? "1" : "0"} data-tem-sessao={temSessao ? "1" : "0"}>
      <div className="flex flex-col items-center gap-3 mb-8">
        {logo ? (
          <img src={logo} alt={loja || "Logo da loja"} className="w-20 h-20 rounded-2xl object-cover bg-white shadow-lg" />
        ) : (
          <div className="w-20 h-20 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
            <UtensilsCrossed className="w-10 h-10" />
          </div>
        )}
        {loja ? <p className="text-lg font-black tracking-tight">{loja}</p> : null}
      </div>
      <p className="text-sm uppercase tracking-widest opacity-60">Você já está na</p>
      <h1 className="text-5xl font-black mt-1 mb-5" data-e2e="mesa-outra-minha">{minha}</h1>
      {podeMudar ? (
        <p className="text-xl font-black mb-2" data-e2e="mesa-outra-pergunta">Quer mudar para a {esta}?</p>
      ) : null}
      <p className="text-base opacity-70 max-w-xs mb-8" data-e2e="mesa-outra-texto">{explicacao}</p>
      {podeMudar ? (
        <button
          type="button"
          onClick={onMudar}
          disabled={mudando}
          data-e2e="mesa-mudar"
          className="w-full max-w-sm h-16 rounded-full bg-primary text-primary-foreground text-lg font-black shadow-lg flex items-center justify-center gap-2 active:scale-[0.98] transition disabled:opacity-60"
        >
          <ArrowRightLeft className="w-5 h-5" /> {mudando ? "Mudando..." : `MUDAR PARA A ${esta}`}
        </button>
      ) : null}
      <button
        type="button"
        onClick={temSessao ? onVoltar : onFechar}
        disabled={mudando}
        data-e2e={temSessao ? "mesa-voltar-minha" : "mesa-outra-fechar"}
        className={`${podeMudar ? "mt-4 h-12 border-2 text-sm font-bold " + borda : "h-16 bg-primary text-primary-foreground text-lg font-black shadow-lg"} w-full max-w-sm rounded-full flex items-center justify-center gap-2 active:scale-[0.98] transition disabled:opacity-60`}
      >
        <ArrowLeft className={podeMudar ? "w-4 h-4" : "w-5 h-5"} /> {temSessao ? `VOLTAR PARA A ${minha}` : "VOLTAR"}
      </button>
    </div>
  );
}
